import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditEventInfo, PaginatedAuditEvents } from '@claude-code-api/shared';
import { ChevronLeft, ChevronRight, ClipboardList, Filter, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export default function AdminAuditEventsPage() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [events, setEvents] = useState<AuditEventInfo[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams({ page: String(page), limit: '50' });
    if (appliedSearch) query.set('search', appliedSearch);
    try {
      const response = await fetch(`/api/admin/audit-events?${query.toString()}`, {
        credentials: 'include',
      });
      const result = await response.json() as ApiResponse<PaginatedAuditEvents>;
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('auditEvents.errors.load'));
      }
      setEvents(result.data.events);
      setTotalPages(result.data.pagination.totalPages);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('auditEvents.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, t]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const applyFilters = (): void => {
    setPage(1);
    setAppliedSearch(search.trim());
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('auditEvents.title')} description={t('auditEvents.description')} />
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9 shadow-none"
            value={search}
            maxLength={100}
            placeholder={t('auditEvents.filters.search')}
            aria-label={t('auditEvents.filters.search')}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyFilters();
            }}
          />
        </div>
        <Button className="shadow-none hover:shadow-none" onClick={applyFilters}>
          <Filter className="mr-2 h-4 w-4" />
          {t('auditEvents.filters.apply')}
        </Button>
      </div>

      {loading ? (
        <div className="grid gap-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      ) : null}
      {!loading && events.length === 0 ? (
        <Card className="shadow-none">
          <CardContent className="py-12 text-center text-muted-foreground">
            <ClipboardList className="mx-auto mb-3 h-8 w-8" />
            {t('auditEvents.empty')}
          </CardContent>
        </Card>
      ) : null}

      {!loading && events.length > 0 ? (
        <div className="overflow-hidden rounded-lg border">
          <Table className="min-w-[1100px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>{t('auditEvents.columns.action')}</TableHead>
                <TableHead>{t('auditEvents.columns.resource')}</TableHead>
                <TableHead>{t('auditEvents.columns.outcome')}</TableHead>
                <TableHead>{t('auditEvents.columns.reason')}</TableHead>
                <TableHead>{t('auditEvents.columns.actor')}</TableHead>
                <TableHead>{t('auditEvents.columns.scope')}</TableHead>
                <TableHead>{t('auditEvents.columns.time')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="max-w-64 break-all font-medium">{event.action}</TableCell>
                  <TableCell><Badge variant="secondary">{event.resourceType}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{event.outcome}</Badge></TableCell>
                  <TableCell className="max-w-80 whitespace-normal break-words text-muted-foreground">
                    {event.reason}
                  </TableCell>
                  <TableCell className="max-w-56 break-all text-muted-foreground">
                    {event.actorEmail ?? event.actorType}
                  </TableCell>
                  <TableCell className="min-w-64 text-xs">
                    <div className="space-y-1">
                      <p className="break-all">
                        <span className="text-muted-foreground">{t('auditEvents.filters.resourceId')}: </span>
                        {event.resourceId ?? '-'}
                      </p>
                      <p className="break-all">
                        <span className="text-muted-foreground">{t('auditEvents.filters.projectId')}: </span>
                        {event.projectId ?? '-'}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    <time dateTime={event.createdAt}>{new Date(event.createdAt).toLocaleString()}</time>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            size="sm"
            variant="outline"
            className="shadow-none hover:shadow-none"
            disabled={page <= 1 || loading}
            onClick={() => setPage((current) => current - 1)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            {t('auditEvents.pagination.previous')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('auditEvents.pagination.pageOf', { page, totalPages })}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="shadow-none hover:shadow-none"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            {t('auditEvents.pagination.next')}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
